import { Alert, Dialog } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { CustomArticleItemToAdd, CustomArticleItemToEdit } from "@tm/data/basket"
import { useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { EditDialogHeader } from "./EditDialogHeader"
import { useSchema } from "./schema"
import { EditDialogBody } from "./EditDialogBody"

export type DialogCustomArticleAdd = CustomArticleItemToAdd & { type: "add" }
export type DialogCustomArticleEdit = CustomArticleItemToEdit & { type: "edit" }
export type DialogCustomArticle = DialogCustomArticleAdd | DialogCustomArticleEdit

type EditDialogProps = {
    article: DialogCustomArticle
    note?: string
    categoryId: number
    saveError?: string
    onClose(): void
    onSave(article: DialogCustomArticle, note?: string): void
}

export function EditDialog({ onClose, onSave, article, note, categoryId, saveError }: EditDialogProps) {
    const { translateText } = useLocalization()
    const schema = useSchema()

    const headline = article.type === "edit" ? translateText(757) : translateText(13906)

    const defaultValues = useMemo(() => {
        if (article.type === "add") {
            return {
                description: "",
                vatType: 0,
            }
        }
        return {
            ...article,
            vatType: article.selectedVatRate.vatType,
            note,
        }
    }, [article, note])

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    })

    const { handleSubmit } = form

    function handleConfirmButtonClick(data: z.infer<typeof schema>) {
        const { note, ...articleData } = data
        onSave({ ...article, ...articleData }, note)
    }

    return (
        <Dialog open position="middle" maxWidth="lg">
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(handleConfirmButtonClick)}>
                    <EditDialogHeader headline={headline} onCloseButtonClick={onClose} sx={{ mb: 2 }} />
                    {saveError && <Alert size="small" severity="error" title={saveError} variant="outlined" sx={{ mb: 2 }} />}
                    <EditDialogBody categoryId={categoryId} />
                </form>
            </FormProvider>
        </Dialog>
    )
}
